import { useCallback, useEffect, useRef, useState } from 'react';
import { commandCenterApi, formatCurrency, formatCurrencyWhole } from '../../utils/api';
import { toast } from '../../components/Toast';
import { LoadState, useRefetchOnFocus } from './parts';
import { monthLabelOf } from './businessMonth';
import { Fee } from './businessFigures';

// לוח תשלומים שנתי — one row per customer, one cell per month.
//
// The question it answers is the one a table of totals cannot: "did פיצה
// רוטשילד pay me in March, and if not, what happened?" A trend line shows the
// shape of a year; this shows the twelve individual facts it is made of, and
// lets you fix any one of them where you noticed it was wrong.
//
// A CALENDAR YEAR, with its own switcher, deliberately not the page's date
// range (owner, 03/09/2026). Two reasons: "what did they pay this year" is a
// calendar question, and a rolling window would give two neighbouring rows
// different spans depending on when each joined — which is exactly the
// comparison a grid invites you to make down a column.
//
// Cell state is carried by FILL AND BY THE FIGURE, never by colour alone: an
// estimate reads "~₪1,450" whether or not you can see its dashed border, and
// the board survives a greyscale print. A note is a corner fold rather than a
// tint, because colour is already spoken for by the payment state and a second
// meaning on the same channel is how a grid becomes unreadable.

const MONTH_ABBR = ['ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יוני', 'יולי', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ'];

/** The years worth offering: this one and the two before it. The product has no
 *  data older than that, and a decade of empty grids is not a feature. */
function yearChoices() {
  const now = new Date().getFullYear();
  return [now - 2, now - 1, now];
}

const STATE_LABEL = {
  paid: 'שולם',
  due: 'לא שולם',
  est: 'לא נרשם חיוב',
  out: 'לא היה לקוח',
  prelaunch: 'PEAK טרם עלתה לאוויר',
};

/** Cells with nothing to open or show: before PEAK went live, or outside the
 *  customer's own relationship. */
const isEmptyCell = (cell) => cell.state === 'out' || cell.state === 'prelaunch';

/**
 * The editor a cell click opens.
 *
 * MINIMISED to amount + paid (owner, 03/09/2026). The board is worked through
 * a month at a time — twelve customers, one column — so the cost that matters
 * is clicks per correction, not fields per dialog. The payment date and the
 * note keep their home in the row's ⋮, which is a deliberate two-tier split
 * rather than a feature dropped: the common edit is two controls and a save,
 * and the rare one is one menu away.
 */
function CellEditor({ customer, cell, onSaved, onClose, anchor }) {
  const [amount, setAmount] = useState(String(cell.amount ?? 0));
  const [paid, setPaid] = useState(!!cell.paid);
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    boxRef.current?.querySelector('input')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    const next = Number(amount);
    if (!Number.isFinite(next) || next < 0) {
      toast('סכום לא תקין', 'error');
      return;
    }
    setSaving(true);
    try {
      // The note is NOT sent. Omitting it keeps whatever is on the row — a
      // minimal editor must not silently blank a field it never showed.
      await commandCenterApi.saveCrmCharge(customer.id, cell.month, { amount: next, paid });
      onSaved();
    } catch (err) {
      toast(err.response?.data?.error || 'שמירת החיוב נכשלה', 'error');
      setSaving(false);
    }
  };

  return (
    <>
      <div className="cc-cell-backdrop" onClick={onClose} />
      <div
        className="cc-cell-pop"
        ref={boxRef}
        role="dialog"
        aria-label={`עריכת חיוב — ${customer.name}, ${monthLabelOf(cell.month)}`}
        style={anchor}
        onClick={e => e.stopPropagation()}
      >
        <div className="cc-cell-pop-head">
          <strong>{customer.name}</strong>
          <span className="cc-muted">{monthLabelOf(cell.month)}</span>
        </div>
        <form onSubmit={submit}>
          <div className="form-group">
            <label htmlFor="cell-amount">סכום (₪)</label>
            <input
              id="cell-amount"
              className="form-control"
              type="number"
              min="0"
              step="1"
              inputMode="decimal"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
          </div>
          <label className="cc-check">
            <input type="checkbox" checked={paid} onChange={e => setPaid(e.target.checked)} />
            התקבל תשלום
          </label>
          {/* Says where the rest lives, so the missing fields read as a choice
              rather than as an editor that forgot them. */}
          <p className="cc-cell-pop-more">
            תאריך תשלום והערה — בתפריט השורה ברשימת הלקוחות.
          </p>
          <div className="cc-cell-pop-actions">
            <button type="submit" className="btn btn-sm btn-success" disabled={saving}>
              {saving ? 'שומר…' : 'שמור'}
            </button>
            <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>ביטול</button>
          </div>
        </form>
      </div>
    </>
  );
}

export default function PaymentBoard() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  // { customer, cell, anchor } — the cell whose editor is open.
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await commandCenterApi.getCrmBoard(year);
      setData(r.data);
    } catch {
      setError(true);
      setData(null);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);

  const openCell = (customer, cell, el) => {
    if (isEmptyCell(cell)) return;
    const r = el.getBoundingClientRect();
    setEditing({
      customer,
      cell,
      anchor: {
        top: r.bottom + window.scrollY + 6,
        // Clamped so a cell at either edge of a scrolled grid still opens a
        // dialog that is fully on screen.
        left: Math.min(Math.max(8, r.left + window.scrollX + r.width / 2 - 125), window.innerWidth - 258),
      },
    });
  };

  const customers = data?.customers || [];

  return (
    <div className="cc-card">
      <div className="cc-trendcard-head">
        <div className="cc-trendcard-heading">
          <h2 className="cc-card-title">לוח תשלומים שנתי</h2>
          <div className="cc-card-sub">
            שורה ללקוח, עמודה לחודש · לחיצה על תא עורכת את החיוב · שנה קלנדרית, לא תלוי בטווח שלמעלה
          </div>
        </div>
        <div className="cc-years" role="group" aria-label="שנה">
          {yearChoices().map(y => (
            <button
              key={y}
              type="button"
              className={`cc-year${y === year ? ' is-on' : ''}`}
              aria-pressed={y === year}
              onClick={() => { setEditing(null); setYear(y); }}
            >{y}</button>
          ))}
        </div>
      </div>

      <div className="cc-legend">
        <span className="cc-legend-item"><i className="cc-key is-paid" /> שולם</span>
        <span className="cc-legend-item"><i className="cc-key is-due" /> לא שולם</span>
        <span className="cc-legend-item"><i className="cc-key is-est" /> לא נרשם חיוב</span>
        <span className="cc-legend-item"><i className="cc-key is-out" /> לא היה לקוח</span>
        {customers.some(c => c.cells.some(cell => cell.state === 'prelaunch')) && (
          <span className="cc-legend-item"><i className="cc-key is-prelaunch" /> PEAK טרם עלתה לאוויר</span>
        )}
        <span className="cc-legend-item"><i className="cc-key is-paid has-note" /> יש הערה</span>
      </div>

      <LoadState
        loading={data === null && !error}
        error={error}
        onRetry={load}
        empty={!!data && customers.length === 0}
        emptyMessage={`אין לקוחות בשנת ${year}`}
      >
        <div className="table-wrapper cc-board-scroll">
          <table className="cc-board">
            <thead>
              <tr>
                <th className="cc-board-name">לקוח</th>
                {MONTH_ABBR.map(m => <th key={m}>{m}</th>)}
                <th className="cc-board-total">סה״כ {year}</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => (
                <tr key={c.id}>
                  <th scope="row" className="cc-board-name">
                    <span className="cc-board-cust">
                      {c.name}
                      {c.ended_month && <span className="cc-board-left">עזב</span>}
                    </span>
                    <span className="cc-board-fee">
                      <Fee amount={c.monthly_fee} entered={c.monthly_fee_entered} suffix=" לחודש" />
                    </span>
                  </th>
                  {c.cells.map(cell => (
                    <td key={cell.month}>
                      <button
                        type="button"
                        className={[
                          'cc-cell',
                          `is-${cell.state}`,
                          cell.note ? 'has-note' : '',
                          cell.is_join ? 'is-join' : '',
                          cell.is_last ? 'is-last' : '',
                        ].filter(Boolean).join(' ')}
                        disabled={isEmptyCell(cell)}
                        title={cell.state === 'prelaunch' ? STATE_LABEL.prelaunch : (cell.note || undefined)}
                        aria-label={
                          `${c.name} — ${monthLabelOf(cell.month)}: ${STATE_LABEL[cell.state]}`
                          + (isEmptyCell(cell) ? '' : ` ${formatCurrency(cell.amount)}`)
                          + (cell.note ? `. הערה: ${cell.note}` : '')
                        }
                        onClick={(e) => openCell(c, cell, e.currentTarget)}
                      >
                        {!isEmptyCell(cell) && (
                          <>
                            <span className="cc-cell-amount">
                              {cell.state === 'est' ? '~' : ''}{formatCurrencyWhole(cell.amount)}
                            </span>
                            {cell.is_join
                              ? <span className="cc-cell-tick is-join">הצטרף</span>
                              : cell.is_last
                                ? <span className="cc-cell-tick is-last">עזב</span>
                                : cell.state === 'paid' ? <span className="cc-cell-tick">שולם</span> : null}
                          </>
                        )}
                      </button>
                    </td>
                  ))}
                  <td className="cc-board-total">
                    <span className="cc-board-paid">{formatCurrencyWhole(c.paid)}</span>
                    <span className="cc-board-due">
                      {c.due ? `לגבייה ${formatCurrencyWhole(c.due)}` : 'הכול נגבה'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" className="cc-board-name">נגבה בחודש</th>
                {(data?.month_collected || []).map((v, i) => (
                  <td key={MONTH_ABBR[i]}>{v ? formatCurrencyWhole(v) : <span className="cc-muted">—</span>}</td>
                ))}
                <td className="cc-board-total">
                  <span className="cc-board-paid">{formatCurrencyWhole(data?.total_collected || 0)}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </LoadState>

      {editing && (
        <CellEditor
          customer={editing.customer}
          cell={editing.cell}
          anchor={editing.anchor}
          onSaved={() => {
            toast(`החיוב ל${monthLabelOf(editing.cell.month)} נשמר`);
            setEditing(null);
            load();
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
